import { formatDateDDMMYYYY } from '../utils/dateFormat';

const DEFAULT_TAX_RATE_PERCENT = 14;

const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatCurrency = (value, locale) => {
  const numericValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  return new Intl.NumberFormat(locale || 'en-US', {
    style: 'currency',
    currency: 'EGP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

const formatNumber = (value, locale) => {
  const numericValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  return new Intl.NumberFormat(locale || 'en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

const formatDateValue = (value) => {
  if (!value) {
    return '-';
  }
  try {
    return formatDateDDMMYYYY(value, '-');
  } catch (error) {
    return escapeHtml(value);
  }
};

const getDealDateValue = (deal) => deal?.dealDate || deal?.createdAt;

const getPrintStyles = () => `
  @page {
    margin: 32px;
  }

  body {
    font-family: 'Segoe UI', Arial, sans-serif;
    color: #1f2933;
    margin: 0;
    padding: 32px;
    background: #ffffff;
  }

  h1, h2, h3, h4 {
    margin: 0;
    font-weight: 700;
    color: #0f6a3e;
  }

  p {
    margin: 4px 0;
    line-height: 1.4;
  }

  .print-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .print-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding-bottom: 16px;
    border-bottom: 2px solid rgba(15, 106, 62, 0.2);
  }

  .print-logo {
    height: 70px;
    object-fit: contain;
  }

  .print-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .print-section h2 {
    font-size: 18px;
    border-bottom: 1px solid rgba(15, 106, 62, 0.18);
    padding-bottom: 6px;
  }

  .print-info-table,
  .print-table,
  .print-summary-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  .print-info-table th,
  .print-info-table td {
    text-align: left;
    padding: 6px 10px;
  }

  .print-row-number {
    width: 42px;
    text-align: center !important;
    font-weight: 700;
    color: #0f6a3e;
    background: rgba(15, 106, 62, 0.06);
  }

  .print-info-table th {
    width: 220px;
    color: #0f6a3e;
    background: rgba(15, 106, 62, 0.08);
    font-weight: 600;
  }

  .print-table th,
  .print-table td,
  .print-summary-table th,
  .print-summary-table td {
    border: 1px solid rgba(0, 0, 0, 0.12);
    padding: 8px 10px;
    text-align: left;
  }

  .print-table th {
    background: rgba(15, 106, 62, 0.1);
    font-weight: 700;
  }

  .print-summary-table {
    max-width: 320px;
    margin-left: auto;
  }

  .print-summary-table th {
    background: rgba(15, 106, 62, 0.08);
    font-weight: 600;
  }

  .print-note {
    font-size: 12px;
    color: #4b5563;
    margin-top: 8px;
  }

  .signature-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
    margin-top: 12px;
  }

  .signature-box {
    padding-top: 36px;
    border-top: 1px solid rgba(0, 0, 0, 0.45);
    text-align: center;
    font-size: 12px;
    color: #374151;
    min-height: 80px;
  }

  .print-footer {
    font-size: 11px;
    color: #6b7280;
    text-align: right;
    margin-top: 24px;
  }
`;

const normaliseNumber = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }
  return numeric;
};

const normalizeTaxRatePercent = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return DEFAULT_TAX_RATE_PERCENT;
  }
  return Math.min(100, numericValue);
};

const buildPrintLogo = (logoSrc, includeLogo = true) =>
  includeLogo && logoSrc
    ? `<img src="${logoSrc}" alt="Logo" class="print-logo" />`
    : '';

export const computeDealFinancials = (
  deal = {},
  applyTaxesOverride,
  taxRatePercent = DEFAULT_TAX_RATE_PERCENT
) => {
  const products = Array.isArray(deal.products) ? deal.products : [];
  const normalizedTaxRatePercent = normalizeTaxRatePercent(taxRatePercent);

  const productSummaries = products.map((product) => {
    const quantity = normaliseNumber(product.quantity);
    const unitPrice = normaliseNumber(product.pricePerItem);
    const pricePerQuantity = normaliseNumber(product.pricePerQuantity);
    const discountPercent = Math.min(
      100,
      Math.max(0, Number(product.discount ?? 0))
    );
    const effectiveUnitPrice =
      pricePerQuantity > 0 ? pricePerQuantity : unitPrice;
    const baseAmount = quantity * effectiveUnitPrice;
    const discountAmount = baseAmount * (discountPercent / 100);
    const netAmount = baseAmount - discountAmount;

    return {
      productName: product.productName || '',
      SKU: product.SKU || '',
      sort: product.sort || '',
      color: product.color || '',
      quantity,
      unitPrice,
      pricePerQuantity,
      effectiveUnitPrice,
      discountPercent,
      baseAmount,
      discountAmount,
      netAmount,
    };
  });

  const subtotal = productSummaries.reduce(
    (sum, item) => sum + item.netAmount,
    0
  );
  const dealDiscountPercent = Math.min(
    100,
    Math.max(0, Number(deal.dealDiscount ?? 0))
  );
  const dealDiscountAmount = subtotal * (dealDiscountPercent / 100);
  const subtotalAfterDiscount = subtotal - dealDiscountAmount;

  const applyTaxes =
    typeof applyTaxesOverride === 'boolean'
      ? applyTaxesOverride
      : Boolean(deal.applyTaxes);
  const taxAmount = subtotalAfterDiscount * (normalizedTaxRatePercent / 100);
  const appliedTaxAmount = applyTaxes ? taxAmount : 0;
  const totalWithoutTax = subtotalAfterDiscount;
  const totalWithTax = subtotalAfterDiscount + taxAmount;
  const totalDue = applyTaxes ? totalWithTax : totalWithoutTax;

  return {
    products: productSummaries,
    subtotal,
    dealDiscountPercent,
    dealDiscountAmount,
    subtotalAfterDiscount,
    taxAmount,
    appliedTaxAmount,
    taxRatePercent: normalizedTaxRatePercent,
    totalWithoutTax,
    totalWithTax,
    totalDue,
    applyTaxes,
  };
};

const buildProductsTable = (summary, labels, locale) => {
  if (!summary.products.length) {
    return `<p class="print-note">${escapeHtml(labels.noProducts || '')}</p>`;
  }

  return `
    <table class="print-table">
      <thead>
        <tr>
          <th>#</th>
          <th>${escapeHtml(labels.product)}</th>
          <th>${escapeHtml(labels.sku)}</th>
          <th>${escapeHtml(labels.sort)}</th>
          <th>${escapeHtml(labels.color)}</th>
          <th>${escapeHtml(labels.quantity)}</th>
          <th>${escapeHtml(labels.unitPrice)}</th>
          <th>${escapeHtml(labels.pricePerQuantityShort)}</th>
          <th>${escapeHtml(labels.discount)}</th>
          <th>${escapeHtml(labels.lineTotal)}</th>
        </tr>
      </thead>
      <tbody>
        ${summary.products
          .map(
            (item, index) => `
              <tr>
                <td class="print-row-number">${index + 1}</td>
                <td>${escapeHtml(item.productName)}</td>
                <td>${escapeHtml(item.SKU)}</td>
                <td>${escapeHtml(item.sort)}</td>
                <td>${escapeHtml(item.color)}</td>
                <td>${formatNumber(item.quantity, locale)}</td>
                <td>${formatCurrency(item.unitPrice, locale)}</td>
                <td>${
                  item.pricePerQuantity > 0
                    ? formatCurrency(item.pricePerQuantity, locale)
                    : '-'
                }</td>
                <td>${formatNumber(item.discountPercent, locale)}%</td>
                <td>${formatCurrency(item.netAmount, locale)}</td>
              </tr>
            `
          )
          .join('')}
      </tbody>
    </table>
  `;
};

const buildDealTotalsTable = (summary, labels, locale) => `
  <table class="print-summary-table">
    <tbody>
      <tr>
        <td class="print-row-number">1</td>
        <th>${escapeHtml(labels.subtotal)}</th>
        <td>${formatCurrency(summary.subtotal, locale)}</td>
      </tr>
      <tr>
        <td class="print-row-number">2</td>
        <th>${escapeHtml(labels.dealDiscount)} (${formatNumber(
  summary.dealDiscountPercent,
  locale
)}%)</th>
        <td>${formatCurrency(summary.dealDiscountAmount, locale)}</td>
      </tr>
      <tr>
        <td class="print-row-number">3</td>
        <th>${escapeHtml(labels.totalBeforeTax || labels.totalAfterDiscount)}</th>
        <td>${formatCurrency(summary.subtotalAfterDiscount, locale)}</td>
      </tr>
      <tr>
        <td class="print-row-number">4</td>
        <th>${escapeHtml(labels.taxAmount || labels.taxes)}</th>
        <td>${
          summary.applyTaxes
            ? formatCurrency(summary.taxAmount, locale)
            : escapeHtml(labels.taxNotApplied)
        }</td>
      </tr>
      <tr>
        <td class="print-row-number">5</td>
        <th>${escapeHtml(labels.totalWithoutTax)}</th>
        <td>${formatCurrency(summary.totalWithoutTax, locale)}</td>
      </tr>
      <tr>
        <td class="print-row-number">6</td>
        <th>${escapeHtml(labels.totalWithTax)}</th>
        <td>${formatCurrency(summary.totalWithTax, locale)}</td>
      </tr>
      <tr>
        <td class="print-row-number">7</td>
        <th>${escapeHtml(labels.totalDue)}</th>
        <td>${formatCurrency(summary.totalDue, locale)}</td>
      </tr>
    </tbody>
  </table>
`;

const buildSignatureSection = (labels) => `
  <div class="signature-row">
    <div class="signature-box">${escapeHtml(labels.recipientName)}</div>
    <div class="signature-box">${escapeHtml(labels.recipientId)}</div>
    <div class="signature-box">${escapeHtml(labels.signature)}</div>
  </div>
`;

export const buildDealPrintHtml = ({
  customer,
  deal,
  options,
  labels,
  logoSrc,
  locale,
}) => {
  const printLocale = locale || 'en-US';
  const summary = computeDealFinancials(
    deal,
    options.applyTaxes,
    options.taxRatePercent
  );
  const dealDate = formatDateValue(
    options.printDate || getDealDateValue(deal),
    printLocale
  );
  const printedOn = formatDateValue(new Date(), printLocale);
  const includeLogo = summary.applyTaxes;

  return `
    <div class="print-wrapper">
      <header class="print-header">
        ${buildPrintLogo(logoSrc, includeLogo)}
        <div>
          <h1>${escapeHtml(labels.dealSummaryTitle)}</h1>
          <p>${escapeHtml(customer?.organizationName || '')}</p>
          <p>${escapeHtml(labels.printedOn)}: ${escapeHtml(printedOn)}</p>
        </div>
      </header>

      <section class="print-section">
        <h2>${escapeHtml(labels.customerDetails)}</h2>
        <table class="print-info-table">
          <tbody>
            <tr>
              <td class="print-row-number">1</td>
              <th>${escapeHtml(labels.customerId)}</th>
              <td>${escapeHtml(customer?.customerID || '-')}</td>
            </tr>
            <tr>
              <td class="print-row-number">2</td>
              <th>${escapeHtml(labels.organization)}</th>
              <td>${escapeHtml(customer?.organizationName || '-')}</td>
            </tr>
            <tr>
              <td class="print-row-number">3</td>
              <th>${escapeHtml(labels.contact)}</th>
              <td>${escapeHtml(customer?.contactPerson || '-')}</td>
            </tr>
            <tr>
              <td class="print-row-number">4</td>
              <th>${escapeHtml(labels.phone)}</th>
              <td>${escapeHtml(customer?.phoneNumber || '-')}</td>
            </tr>
            <tr>
              <td class="print-row-number">5</td>
              <th>${escapeHtml(labels.address)}</th>
              <td>${escapeHtml(customer?.address || '-')}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="print-section">
        <h2>${escapeHtml(labels.dealDetails)}</h2>
        <table class="print-info-table">
          <tbody>
            <tr>
              <td class="print-row-number">1</td>
              <th>${escapeHtml(labels.dealDate)}</th>
              <td>${escapeHtml(dealDate)}</td>
            </tr>
            <tr>
              <td class="print-row-number">2</td>
              <th>${escapeHtml(labels.preparedBy)}</th>
              <td>${escapeHtml(options.preparedBy || '-')}</td>
            </tr>
            <tr>
              <td class="print-row-number">3</td>
              <th>${escapeHtml(labels.includeTaxes)}</th>
              <td>${
                summary.applyTaxes
                  ? escapeHtml(labels.taxes)
                  : escapeHtml(labels.taxNotApplied)
              }</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="print-section">
        <h2>${escapeHtml(labels.productsTitle)}</h2>
        ${buildProductsTable(summary, labels, printLocale)}
      </section>

      <section class="print-section">
        <h2>${escapeHtml(labels.dealTotals)}</h2>
        ${buildDealTotalsTable(summary, labels, printLocale)}
        ${
          labels.applyTaxesNote
            ? `<p class="print-note">${escapeHtml(labels.applyTaxesNote)}</p>`
            : ''
        }
      </section>

      <section class="print-section">
        <h2>${escapeHtml(labels.recipientDetails)}</h2>
        <table class="print-info-table">
          <tbody>
            <tr>
              <td class="print-row-number">1</td>
              <th>${escapeHtml(labels.recipientName)}</th>
              <td>${escapeHtml(options.recipientName || '')}</td>
            </tr>
            <tr>
              <td class="print-row-number">2</td>
              <th>${escapeHtml(labels.recipientId)}</th>
              <td>${escapeHtml(options.recipientId || '')}</td>
            </tr>
          </tbody>
        </table>
        ${
          options.notes
            ? `<p class="print-note">${escapeHtml(options.notes)}</p>`
            : ''
        }
        ${buildSignatureSection(labels)}
      </section>
    </div>
  `;
};

export const buildSelectedDealsHtml = ({
  customer,
  deals,
  labels,
  logoSrc,
  locale,
  taxRatePercent,
}) => {
  const printLocale = locale || 'en-US';
  const printedOn = formatDateValue(new Date(), printLocale);
  const selectedDeals = Array.isArray(deals) ? deals : [];
  const includeLogo = selectedDeals.some((deal) =>
    computeDealFinancials(deal, undefined, taxRatePercent).applyTaxes
  );

  const dealsContent = selectedDeals
    .map((deal, index) => {
      const summary = computeDealFinancials(deal, undefined, taxRatePercent);
      const dealDate = formatDateValue(getDealDateValue(deal), printLocale);
      return `
        <section class="print-section">
          <h2>${escapeHtml(labels.dealSummaryTitle)} #${index + 1}</h2>
          <p>${escapeHtml(labels.dealDate)}: ${escapeHtml(dealDate)}</p>
          ${buildProductsTable(summary, labels, printLocale)}
          ${buildDealTotalsTable(summary, labels, printLocale)}
        </section>
      `;
    })
    .join('');

  return `
    <div class="print-wrapper">
      <header class="print-header">
        ${buildPrintLogo(logoSrc, includeLogo)}
        <div>
          <h1>${escapeHtml(labels.selectedDealsTitle)}</h1>
          <p>${escapeHtml(customer?.organizationName || '')}</p>
          <p>${escapeHtml(labels.printedOn)}: ${escapeHtml(printedOn)}</p>
        </div>
      </header>

      <section class="print-section">
        <h2>${escapeHtml(labels.customerDetails)}</h2>
        <table class="print-info-table">
          <tbody>
            <tr>
              <td class="print-row-number">1</td>
              <th>${escapeHtml(labels.customerId)}</th>
              <td>${escapeHtml(customer?.customerID || '-')}</td>
            </tr>
            <tr>
              <td class="print-row-number">2</td>
              <th>${escapeHtml(labels.organization)}</th>
              <td>${escapeHtml(customer?.organizationName || '-')}</td>
            </tr>
            <tr>
              <td class="print-row-number">3</td>
              <th>${escapeHtml(labels.contact)}</th>
              <td>${escapeHtml(customer?.contactPerson || '-')}</td>
            </tr>
            <tr>
              <td class="print-row-number">4</td>
              <th>${escapeHtml(labels.phone)}</th>
              <td>${escapeHtml(customer?.phoneNumber || '-')}</td>
            </tr>
          </tbody>
        </table>
      </section>

      ${dealsContent}
    </div>
  `;
};

export const buildCustomerProfileHtml = ({
  customer,
  overviewEntries,
  labels,
  logoSrc,
  locale,
  taxRatePercent,
}) => {
  const printLocale = locale || 'en-US';
  const printedOn = formatDateValue(new Date(), printLocale);
  const deals = Array.isArray(customer?.deals) ? customer.deals : [];

  const overviewRows = (overviewEntries || [])
    .map(
      (entry, index) => `
        <tr>
          <td class="print-row-number">${index + 1}</td>
          <th>${escapeHtml(entry.label)}</th>
          <td>${escapeHtml(entry.value ?? '-')}</td>
        </tr>
      `
    )
    .join('');

  const dealRows = deals
    .map((deal, index) => {
      const summary = computeDealFinancials(deal, undefined, taxRatePercent);
      return `
        <tr>
          <td class="print-row-number">${index + 1}</td>
          <td>${escapeHtml(formatDateValue(getDealDateValue(deal), printLocale))}</td>
          <td>${formatNumber(deal.products?.length || 0, printLocale)}</td>
          <td>${formatCurrency(summary.subtotal, printLocale)}</td>
          <td>${formatCurrency(summary.totalDue, printLocale)}</td>
        </tr>
      `;
    })
    .join('');

  const totalDealsValue = deals.reduce((acc, deal) => {
    const summary = computeDealFinancials(deal, undefined, taxRatePercent);
    return acc + summary.totalDue;
  }, 0);

  return `
    <div class="print-wrapper">
      <header class="print-header">
        ${buildPrintLogo(logoSrc)}
        <div>
          <h1>${escapeHtml(labels.printCustomerProfileTitle)}</h1>
          <p>${escapeHtml(customer?.organizationName || '')}</p>
          <p>${escapeHtml(labels.printedOn)}: ${escapeHtml(printedOn)}</p>
        </div>
      </header>

      <section class="print-section">
        <h2>${escapeHtml(labels.profileOverview)}</h2>
        <table class="print-info-table">
          <tbody>
            ${overviewRows}
          </tbody>
        </table>
      </section>

      <section class="print-section">
        <h2>${escapeHtml(labels.dealsOverview)}</h2>
        <p>${escapeHtml(labels.totalDeals)}: ${formatNumber(
    deals.length,
    printLocale
  )}</p>
        <table class="print-table">
          <thead>
            <tr>
              <th>#</th>
              <th>${escapeHtml(labels.dealDate)}</th>
              <th>${escapeHtml(labels.productsCount)}</th>
              <th>${escapeHtml(labels.subtotal)}</th>
              <th>${escapeHtml(labels.totalDue)}</th>
            </tr>
          </thead>
          <tbody>
            ${dealRows}
          </tbody>
        </table>
        <div class="print-note">
          ${escapeHtml(labels.totalDue)}: ${formatCurrency(
    totalDealsValue,
    printLocale
  )}
        </div>
      </section>
    </div>
  `;
};

export const openPrintWindow = ({ title, content }) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>${getPrintStyles()}</style>
      </head>
      <body>
        ${content}
      </body>
    </html>
  `;

  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.setAttribute('aria-hidden', 'true');
  document.body.appendChild(frame);

  const cleanup = () => {
    if (frame.parentNode) {
      frame.parentNode.removeChild(frame);
    }
  };

  const frameWindow = frame.contentWindow;
  if (!frameWindow) {
    cleanup();
    console.error('Failed to access print frame.');
    return;
  }

  const frameDoc = frameWindow.document;
  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  const handleAfterPrint = () => {
    frameWindow.removeEventListener('afterprint', handleAfterPrint);
    cleanup();
  };

  frameWindow.addEventListener('afterprint', handleAfterPrint);

  // Allow time for layout/paint before printing.
  setTimeout(() => {
    try {
      frameWindow.focus();
      frameWindow.print();
    } catch (error) {
      console.error('Failed to trigger print:', error);
      cleanup();
    }
  }, 200);
};
